import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Loader2, CheckCircle2, XCircle, Crown } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_ATTEMPTS = 8;

export default function PaymentResult({ cancelled = false }) {
  const [state, setState] = useState(cancelled ? "cancelled" : "checking");
  const attempts = useRef(0);

  useEffect(() => {
    if (cancelled) return;
    const sessionId = new URLSearchParams(window.location.search).get("session_id");
    if (!sessionId) {
      setState("error");
      return;
    }
    const poll = async () => {
      attempts.current += 1;
      try {
        const r = await axios.get(`${API}/payments/status/${sessionId}`);
        if (r.data.payment_status === "paid") {
          setState("success");
          return;
        }
        if (["failed", "expired"].includes(r.data.payment_status)) {
          setState("error");
          return;
        }
      } catch {
        // keep polling
      }
      if (attempts.current >= MAX_ATTEMPTS) {
        setState("timeout");
      } else {
        setTimeout(poll, 2500);
      }
    };
    poll();
  }, [cancelled]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl shadow-xl p-10 text-center max-w-md w-full">
        {state === "checking" && (
          <div data-testid="payment-checking">
            <Loader2 className="w-12 h-12 text-purple-600 animate-spin mx-auto mb-4" />
            <h2 className="text-xl font-bold text-gray-900 mb-2">Verificando tu pago...</h2>
            <p className="text-gray-600 text-sm">No cierres esta ventana.</p>
          </div>
        )}
        {state === "success" && (
          <div data-testid="payment-success">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center mx-auto mb-4">
              <Crown className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">¡Ya eres Pro! 🎉</h2>
            <p className="text-gray-600 mb-6">Tu pago se ha completado. Disfruta de análisis ilimitados, comparación de videos y todas las funciones premium.</p>
            <a href="/" data-testid="payment-success-home" className="block w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white px-6 py-3 rounded-xl font-semibold hover:from-purple-700 hover:to-pink-700 transition-all">
              Empezar a analizar sin límites
            </a>
          </div>
        )}
        {state === "cancelled" && (
          <div data-testid="payment-cancelled">
            <XCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-gray-900 mb-2">Pago cancelado</h2>
            <p className="text-gray-600 mb-6">No se realizó ningún cargo. Puedes intentarlo de nuevo cuando quieras.</p>
            <a href="/precios" className="block w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white px-6 py-3 rounded-xl font-semibold transition-all">Ver planes</a>
            <a href="/" className="block mt-3 text-sm text-purple-600 hover:underline">Volver a la app</a>
          </div>
        )}
        {(state === "error" || state === "timeout") && (
          <div data-testid="payment-error">
            <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-gray-900 mb-2">
              {state === "timeout" ? "El pago sigue procesándose" : "No pudimos confirmar el pago"}
            </h2>
            <p className="text-gray-600 mb-6">
              {state === "timeout"
                ? "Si el cargo se completó, tu cuenta se actualizará automáticamente en unos minutos."
                : <>Si crees que es un error, <a href={`mailto:${process.env.REACT_APP_SUPPORT_EMAIL}`} data-testid="payment-support-link" className="underline hover:text-gray-900">contáctanos</a> y lo revisaremos.</>}
            </p>
            <a href="/" className="block w-full bg-gray-900 text-white px-6 py-3 rounded-xl font-semibold hover:bg-gray-800 transition-all">Volver a la app</a>
          </div>
        )}
        {state === "success" && (
          <p className="mt-4 text-xs text-gray-400 flex items-center justify-center gap-1">
            <CheckCircle2 className="w-3 h-3" /> Pago procesado de forma segura con Stripe
          </p>
        )}
      </div>
    </div>
  );
}
